// DEPENDENCY WARNING: This file is also used in native problem question webviews. If you add a
// new dependency here, make sure a) that it's absolutely necessary and b) that codex in webviews
// still work.
import $ from 'jquery';
import * as codexEditor from 'app/codex/codemirror';
import { getSharedContext } from 'app/codex/shared_context';
import { handleOutputGifs } from 'app/codex/utils';
import ajax from 'util/ajax';
import { getToken } from 'util/csrf';
import { Anchors } from 'util/ui';
import {
    CLASS_NAME_ACTIVE,
    CLASS_NAME_HIDE,
    CLASS_NAME_DISABLED,
    EVT_DAILYPROBLEM_MAINBODY_EXPANDED,
} from 'util/constants';
import { jqGetElemById, $body, $window, isNative, isAndroid, isIos, appVersion } from 'util/global';
import { getFormData } from 'util/getFormData';
import { appendGetParams } from 'util/urls';

/**
 * Always use the same page key
 * @type {String}
 */
const CODEX_PAGE_KEY = 'codex_page';
const CODEX_COMPONENT = `cmp_${CODEX_PAGE_KEY}_id`;

const MIN_HEIGHT = 200;
const MAX_INITIAL_HEIGHT = 810;
const BOTTOM_PADDING = 30;

/**
 * Copied from util/ui (showConfirmationModal) to avoid having to import the whole module :(
 * @param {string} msg
 * @param {object} opts
 * @param {Function} callback
 */
function codexConfirm($parent, msg, opts, callback) {
    const $existingModal = $('#confirm-modal');
    if ($existingModal.length > 0) {
        $existingModal.remove();
    }

    let confirmed = false;
    const $confirmModal = $(`
        <div class="modal hide codex-confirm" id="confirm-modal">
            <div class="modal-body"><p>${msg}</p></div>
            <div class="modal-footer">
                <a href="#" class="btn cancel" data-dismiss="modal">${opts.cancel_text}</a>
                <a href="#" class="btn btn-accent confirm">${opts.ok_text}</a>
            </div>
        </div>
    `);

    $confirmModal.find('.confirm').on('click', (e) => {
        e.preventDefault();
        confirmed = true;
        $confirmModal.modal('hide');
        $confirmModal.trigger($.Event('codex.hide'));
    });

    $confirmModal.on('hide', () => callback(confirmed));

    $parent.find('div:first').after().append($confirmModal);
    $confirmModal.modal();
}

/**
 * Handler for resizing the height of the codex
 * @param {JQuery} $resize - element that resizes on drag
 * @param {CodeMirror} codemirror - instance to call setSize on: https://codemirror.net/doc/manual.html#api_sizing
 * @param {JQuery} $editor - for getting computed height
 */
function resizeHandler($resize, codemirror, $editor) {
    // set height to length of the code + padding between 200 and 810px for web (mobile and desktop)
    const documentHeight = parseInt(codemirror.display.sizer.style.minHeight, 10);
    // if native, set codex to full height to avoid scroll issues
    if (isNative) {
        codemirror.setSize(null, `${documentHeight + BOTTOM_PADDING}px`);
    } else if (documentHeight < MIN_HEIGHT) {
        codemirror.setSize(null, `${MIN_HEIGHT}px`);
    } else if (documentHeight - BOTTOM_PADDING > MAX_INITIAL_HEIGHT) {
        codemirror.setSize(null, `${MAX_INITIAL_HEIGHT}px`);
    } else {
        codemirror.setSize(null, `${documentHeight + BOTTOM_PADDING}px`);
    }

    // on desktop only
    if ($resize.length > 0) {
        let startY;
        let startH;

        const onDrag = (e) => {
            codemirror.setSize(
                null,
                `${Math.max(MIN_HEIGHT, (startH + e.pageY - startY))}px`
            );
        };

        const onRelease = () => {
            $body.off('mousemove', onDrag);
            $window.off('mouseup', onRelease);
        };

        $resize.on('mousedown', (e) => {
            startY = e.pageY;
            startH = $editor.height();
            $body.on('mousemove', onDrag);
            $window.on('mouseup', onRelease);
        });
    }
}

/**
 * Determines if we need to send codex request info to the client instead of handling the
 * requests in the frontend
 */
const USE_NATIVE_CODEX_REQUEST = (
    (isIos && appVersion.isGreaterThanOrEqualTo('3.4.3'))
    || (isAndroid && appVersion.isGreaterThanOrEqualTo('4.7.0'))
);

/**
 * Use a JS bridge to send ajax request to working around CORS errors when the webview doesn't
 * have an origin (only used for iOS for now)
 * @param {'GET'|'POST'} method Request method
 * @param {object} data Request data
 */
function nativeCodexRequest(method, data) {
    const { url } = data;
    const params = [method];

    if (method === 'POST') {
        params.push(url);
        params.push(JSON.stringify(data));
    } else {
        delete data.url;
        data.page_key = CODEX_PAGE_KEY;
        params.push(appendGetParams(url, data));
    }

    // Send request info to client. Client actually makes the request.
    if (isAndroid) {
        try {
            // eslint-disable-next-line no-undef
            window.Android.codexAjaxRequest(...params);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Unhandled native request', params);
        }
    } else {
        try {
            window.webkit.messageHandlers.main.postMessage(['codexAjaxRequest', ...params]);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Unhandled native request', params);
        }
    }

    // When the client receives a response, it will pass that response to this function
    window.codexAjaxResponse = (response) => {
        const result = typeof response === 'string' ? JSON.parse(response) : response;
        const codexResult = result.actions && result.actions[CODEX_PAGE_KEY];
        if (codexResult) {
            try {
                window.vkey.handle(codexResult, CODEX_PAGE_KEY);
            } catch (e) {
                // ignore
            }
        }
    };
}

/**
 * Codex edit form controller
 * @param {JQuery} $container
 */
export function controller($container) {
    const data = $container.data();

    const $codexParent = $container.parents('.solv-container,#solv-main');
    const $codexComponent = $container.closest(`.${CODEX_COMPONENT}`);

    const isDailyProblem = $container.parents('.dp-problem-main').length > 0;
    if (isDailyProblem && !$codexComponent.is(':visible')) {
        // Wait for hidden codex in DP to be displayed
        $window.one(EVT_DAILYPROBLEM_MAINBODY_EXPANDED, () => controller($container));
        return;
    }

    const $form = $container.find('.codex-form');
    const $runButton = $container.find('.codex-btn-run');
    const $resetButton = $container.find('.codex-view-original');

    const $textarea = $container.find('.codex-textarea textarea');
    const $editor = $container.find('.codex-editor > div');
    const $language = $container.find('[name=language]');

    const initialCode = $container.find('.codex-orig').text();
    const initialLanguage = data.codexLanguage;

    let codexEditorState;

    // handle reset button state transitions
    const checkIfChanged = () => {
        const isUnchanged = codexEditor.isUnchanged(codexEditorState, $language.val());
        const isViewingOrigCode = $resetButton.data('user-code');

        if (!isUnchanged && isViewingOrigCode) {
            $resetButton.removeData('user-code').removeData('user-code');
            $resetButton.removeClass(CLASS_NAME_ACTIVE);
        } else {
            $resetButton.prop('disabled', isUnchanged && !isViewingOrigCode);
        }
    };

    // set container height before refreshing component to prevent the page from jumping
    const setContainerHeight = (reset = false) => {
        $codexParent.each((_, elem) => {
            const $parentItem = $(elem);
            $parentItem.css({
                height: reset === true ? 'auto' : $parentItem.outerHeight(),
            });
        });
    };

    // change classes of other codexes on the page so that only the correct one is refreshed
    const toggleOtherCodex = (ignore) => {
        const codexComponentSelector = `.${CODEX_COMPONENT}`;
        const ignoreClass = `${CODEX_COMPONENT}_ignore`;
        const ignoreSelector = `.${ignoreClass}`;
        if (ignore) {
            $(codexComponentSelector).not($codexComponent)
                .addClass(ignoreClass).removeClass(CODEX_COMPONENT);
        } else {
            $(ignoreSelector).addClass(CODEX_COMPONENT).removeClass(ignoreClass);
        }
    };

    handleOutputGifs($container);

    // init editor
    codexEditor.init({
        $editor,
        $textarea,
        initialCode,
        initialLanguage,
        language: $language.val(),
    }).then((state) => {
        codexEditorState = state;

        resizeHandler($container.find('.resize'), codexEditorState.cm, $editor);

        // get a shared context object if this codex shares is context
        const sharedContext = data.shareContext ? getSharedContext(data.codexEnvId) : null;

        // reset container height after reloading component
        setTimeout(() => setContainerHeight(true));

        // auto-refresh the component if it is currently executing
        if (data.autoRefresh) {
            codexEditor.setReadOnly(codexEditorState);
            $container.find('.CodeMirror-scroll').addClass('disabled');
            setTimeout(() => {
                setContainerHeight();
                const requestData = {
                    solvable_id: data.solvableId,
                    note_id: data.noteId,
                    pane_id: data.paneId,
                    previous_solvable_ids: data.previousSolvableIds,
                    pane_url: data.paneUrl,
                    codex_env_id: data.codexEnvId,
                };

                if (USE_NATIVE_CODEX_REQUEST) {
                    requestData.url = data.cmpUrl.replace('/codex/', '/codex/_native/');
                    requestData.native_component = true;
                    nativeCodexRequest('GET', requestData);
                } else {
                    ajax(CODEX_PAGE_KEY).get(data.cmpUrl, requestData);
                }
            }, 1000);

            return;
        }

        // set other codex back to normal after reloading component
        toggleOtherCodex(false);

        // apply shared context if applicable
        if (sharedContext) {
            sharedContext.applyContext($container, codexEditorState);
        }

        // update the hidden textarea when updating the ace text value
        const smartSingleQuoteRegex = /[\u2018\u2019]/g;
        const smartDoubleQuoteRegex = /[\u201C\u201D]/g;
        codexEditor.onChange(codexEditorState, () => {
            let value = codexEditor.getValue(codexEditorState);

            // replace curly quotes with normal quotes
            if (smartSingleQuoteRegex.test(value) || smartDoubleQuoteRegex.test(value)) {
                value = value
                    .replace(smartSingleQuoteRegex, "'")
                    .replace(smartDoubleQuoteRegex, '"');
                const cursor = codexEditor.getCursor(codexEditorState);
                setTimeout(() => {
                    codexEditor.setValue(codexEditorState, value);
                    codexEditor.setCursor(codexEditorState, cursor);
                });
            }

            // save this value to shared context
            if (sharedContext) {
                sharedContext.setValue(value);
            }

            $textarea.val(codexEditor.getValue(codexEditorState));
            checkIfChanged();
        });

        // Disable codex and show a message when offline
        if (isNative) {
            const enableCodex = (shouldEnable) => {
                if (shouldEnable) {
                    $container.find('.offline-error').removeClass(CLASS_NAME_ACTIVE);
                    $container.find('.codex-email').removeClass(CLASS_NAME_HIDE);
                    $container.find('.codex-btn-run')
                        .removeClass(CLASS_NAME_DISABLED)
                        .prop('disabled', false);
                } else {
                    $container.find('.offline-error').addClass(CLASS_NAME_ACTIVE);
                    $container.find('.codex-email').addClass(CLASS_NAME_HIDE);
                    $container.find('.codex-btn-run')
                        .addClass(CLASS_NAME_DISABLED)
                        .prop('disabled', true);
                }
            };

            window.addEventListener('online', () => enableCodex(true));
            window.addEventListener('offline', () => enableCodex(false));
            if (!window.navigator.onLine) {
                enableCodex(false);
            }
        }

        // process the codex-form div like a form
        $runButton.on('click', () => {
            // clear the shared context output while the code is executing
            if (sharedContext) {
                sharedContext.clearOutput();
            }
            toggleOtherCodex(true);
            setContainerHeight();

            const requestData = {
                url: data.formAction,
                page_key: CODEX_PAGE_KEY,
                cluster: data.formField__cluster,
                param_key: data.formField__param_key || '',
                version: data.formField__version,
                csrfmiddlewaretoken: getToken(),
            };

            if (isNative) {
                requestData.url = requestData.url.replace('/codex/', '/codex/_native/');
                requestData.native_component = true;
            }

            if (USE_NATIVE_CODEX_REQUEST) {
                const allFormData = Object.assign({}, getFormData($form), requestData);
                nativeCodexRequest('POST', allFormData);
            } else {
                ajax(CODEX_PAGE_KEY).form($form[0], requestData);
            }
        });

        // toggle original code/lang and user code/lang
        $resetButton.on('click', (e) => {
            e.preventDefault();
            if (!$resetButton.is(':disabled')) {
                $resetButton.toggleClass(CLASS_NAME_ACTIVE);
                if ($resetButton.hasClass(CLASS_NAME_ACTIVE)) {
                    $resetButton.data('user-code', codexEditor.getValue(codexEditorState));
                    $resetButton.data('user-language', $language.val());
                    codexEditor.setValue(codexEditorState, initialCode);
                    $language.val(initialLanguage);
                } else {
                    const d = $resetButton.data();
                    codexEditor.setValue(codexEditorState, d.userCode);
                    $language.val(d.userLanguage);
                }

                checkIfChanged();
            }
        });

        // update the syntax highlighter mode and refocus when changing the language
        $language.on('change', () => {
            codexEditor.setLanguage(codexEditorState, $language.val());
            codexEditor.focus(codexEditorState);
            checkIfChanged();
        });

        // show hidden labels once everything is ready
        $container.find('header label').show();

        // Use a special handler for Android app 4.2.0 <= version < 4.7.0.
        const useAndroidNoteHandler = (
            isAndroid
            && appVersion
            && appVersion.isGreaterThanOrEqualTo('4.2.0')
            && !appVersion.isGreaterThanOrEqualTo('4.7.0')
        );

        // handle notes
        $container
            .find('.codex-notes')
            .children()
            .addClass(useAndroidNoteHandler ? 'note-placeholder' : 'note');
        if (data.allowNotesEditor) {
            codexEditor.initNoteEditor(codexEditorState, jqGetElemById('id_notes'));
        } else if (data.hasNotes) {
            const noteHandler = codexEditor.initNotes(state, $container, useAndroidNoteHandler);
            noteHandler.run();
        }

        // handle de-emphasizing lines of code
        if (data.allowDeemphasizeEditor) {
            codexEditor.initDeemphasizeEditor({
                state: codexEditorState,
                $deemphasizeField: jqGetElemById('id_deemphasize'),
                $deemphasizeContainer: jqGetElemById('codex-deemphasize-container'),
            });
        } else if (data.deemphasizeData) {
            codexEditor.initDeemphasize({
                state: codexEditorState,
                data: data.deemphasizeData,
                envId: data.codexEnvId,
                initialCode,
            });
        }

        checkIfChanged();

        // codex email button
        const $emailMeContainer = $container.find('.codex-email');
        if ($emailMeContainer.length) {
            $emailMeContainer.find('button').on('click', (e) => {
                e.stopPropagation();
                e.preventDefault();

                const emailMeData = $emailMeContainer.data();

                codexConfirm($emailMeContainer.parents('header'), emailMeData.confirmText, {
                    ok_text: emailMeData.confirmOkText,
                    cancel_text: emailMeData.confirmCancelText,
                }, (confirmed) => {
                    if (confirmed) {
                        $emailMeContainer.removeClass('error');

                        const params = {
                            target_url: emailMeData.targetUrl,
                            target_title: emailMeData.targetTitle,
                        };

                        setTimeout(() => {
                            $.post(`${emailMeData.baseUrl}/codex/_native/email_me/`, params)
                                .fail(() => $emailMeContainer.addClass('error'))
                                .then((response) => {
                                    if (response.success) {
                                        $emailMeContainer.addClass('success');
                                    } else {
                                        $emailMeContainer.addClass('error');
                                    }
                                });
                        }, 100);
                    }
                });
            });
        }
    });
}

/**
 * Holds current state of expandable codex/code blocks
 * @type {Object}
 */
const expandedEnvs = {};

/**
 * Events for handling expandable code blocks
 * @param {JQuery} $container
 */
export function expandableCode($container) {
    const envId = $container.data('codex-env-id') || null;
    const $toggleBtn = $container.find('.codex-expand-toggle');

    const toggleCodeBlock = (e, force) => {
        if (e) {
            e.preventDefault();
        }

        if (force !== undefined) {
            if (envId) {
                expandedEnvs[envId] = force;
            }
            $toggleBtn.toggleClass('active', force);
            $container.toggleClass('expanded', force);
        } else {
            if (envId) {
                expandedEnvs[envId] = !expandedEnvs[envId];
            }
            $toggleBtn.toggleClass('active');
            $container.toggleClass('expanded');
        }

        let margin = '';
        const isExpanded = $container.hasClass('expanded');
        const hasSpecialParent =
            $container.parents('.course-quiz-wrapper,.dp-problem-main').length > 0;
        if (isExpanded && !hasSpecialParent) {
            const wrapperOffset = jqGetElemById('wrapper').offset().left;
            const wrapperPadding = parseInt(jqGetElemById('wrapper').css('padding-left'), 10);
            const containerOffset = $container.offset().left;
            margin = `${(wrapperOffset + wrapperPadding - containerOffset)}px`;
        }
        $container.css('margin-left', margin);
    };

    // show this button here to prevent it showing up where there's no JS
    $toggleBtn.show();

    if (envId) {
        expandedEnvs[envId] = expandedEnvs[envId] || false;
        if (expandedEnvs[envId] === true) {
            toggleCodeBlock(null, true);
        }
    }

    $toggleBtn.on('click', toggleCodeBlock);
}

// We need to stub these for native codex
if (!window.VERSIONS && !window.vkey) {
    window.VERSIONS = window.VERSIONS || {};
    window.vkey = window.vkey || {
        handle(action, actionKey) {
            let $cmp = $(`#cmp_${actionKey}_id`);
            if ($cmp.length === 0) {
                $cmp = $(`.cmp_${actionKey}_id`);
            }
            $cmp.html(action.new_html);

            const $container = $cmp.find('.codex-container');
            $container.attr('data-controller-inited', true);
            controller($container);
        },
    };
} else {
    window.vkey = window.vkey || {};
    window.vkey.codex_page = (action, actionKey) => {
        // Check if the component has returned output, and if so, scroll to it
        const $component = window.vkey.handle(action, actionKey);
        const $output = $component.find('.codex-output');
        if ($output.find('.running').length === 0) {
            Anchors.scrollToTarget($output);
        }
    };
}
